import {
  Alert,
  AlertDescription,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { OverviewTab } from '@client/features/wizard-advisors-metrics/components/overview-tab';
import { AlertCircle } from 'lucide-react';
import { AdvisorsMetricsTable } from '@client/features/wizard-advisors-metrics/components/advisors-metrics-table';
import { useSearchParams } from 'react-router-dom';
import { UsageAnalyticsView } from '../../../pages/AdvisorUsageAnalytics';
import { useAdvisorMetrics } from '../hooks/use-advisor-metrics';
import { useAdvisorUsageMetrics } from '../hooks/use-advisor-usage-metrics';
import { SubscriptionDetailsTable } from './subscription-details-table';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function AdvisorMetricsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const filter = searchParams.get('filter');
  const metrics = useAdvisorMetrics();
  const {
    data: {
      usageMetrics,
      misc: { isLoading, error },
    },
    actions: { getFilteredAdvisorsForUsage, getFilteredAdvisors, getFilteredSubscriptions },
  } = metrics;
  const { advisorsList, activeAdvisorsCount, calculatedMetrics } = useAdvisorUsageMetrics(
    usageMetrics?.usageDetails || {},
    getFilteredAdvisorsForUsage,
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load overview metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="mt-8 flex flex-col items-center justify-center gap-6">
          <LoadingSpinner size={60} />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Metrics"
        description="Comprehensive analytics and insights across all advisors"
      />

      <Tabs
        value={activeTab}
        onValueChange={value => setSearchParams({ tab: value })}
        className="mt-8"
      >
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="advisors">Advisors</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>

        <TabsContent
          value="overview"
          className="mt-6"
        >
          <OverviewTab
            metrics={metrics}
            onMetricClick={(tab: string, filterKey: string) => {
              setSearchParams({ tab, filter: filterKey });
            }}
          />
        </TabsContent>

        <TabsContent
          value="advisors"
          className="mt-6"
        >
          <AdvisorsMetricsTable
            getFilteredAdvisors={getFilteredAdvisors}
            isLoading={isLoading}
            filterKey={filter}
          />
        </TabsContent>

        <TabsContent
          value="subscriptions"
          className="mt-6"
        >
          <SubscriptionDetailsTable
            getFilteredSubscriptions={getFilteredSubscriptions}
            isLoading={isLoading}
            filterKey={filter}
          />
        </TabsContent>

        <TabsContent
          value="usage"
          className="mt-6"
        >
          <UsageAnalyticsView
            activeAdvisorsCount={activeAdvisorsCount}
            isLoading={isLoading}
            calculatedMetrics={calculatedMetrics}
            advisorsList={advisorsList}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
